import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatApiError } from '../utils/api';
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const t = language === 'el' ? {
    title: 'Εγγραφή',
    name: 'Όνομα',
    email: 'Email',
    password: 'Κωδικός (τουλάχιστον 6 χαρακτήρες)',
    submit: 'Εγγραφή',
    submitting: 'Εγγραφή...',
    hasAccount: 'Έχετε ήδη λογαριασμό;',
    login: 'Σύνδεση',
    back: 'Αρχική'
  } : {
    title: 'Sign Up',
    name: 'Name',
    email: 'Email',
    password: 'Password (at least 6 characters)',
    submit: 'Sign Up',
    submitting: 'Signing up...',
    hasAccount: 'Already have an account?',
    login: 'Log In',
    back: 'Home'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors";

  return (
    <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-white/60 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft size={16} className="mr-2" /> {t.back}
        </Link>

        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8">
          <h1 data-testid="register-title" className="text-3xl font-bold text-white mb-8 text-center">{t.title}</h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="relative">
              <User size={18} className="absolute left-3.5 top-3.5 text-white/40" />
              <input data-testid="register-name" type="text" required placeholder={t.name} value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>
            <div className="relative">
              <Mail size={18} className="absolute left-3.5 top-3.5 text-white/40" />
              <input data-testid="register-email" type="email" required placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-3.5 top-3.5 text-white/40" />
              <input data-testid="register-password" type={showPassword ? 'text' : 'password'} required minLength={6} placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-3.5 text-white/40 hover:text-white/70">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && <p data-testid="register-error" className="text-red-400 text-sm text-center">{error}</p>}

            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white" size="lg">
              {loading ? t.submitting : t.submit}
            </Button>
          </form>

          <p className="mt-6 text-center text-white/60 text-sm">
            {t.hasAccount} <Link to="/login" data-testid="login-link" className="text-cyan-400 hover:text-cyan-300 transition-colors">{t.login}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
